import React, { useState, useEffect, useCallback } from 'react'
import {
	VStack,
	HStack,
	FormControl,
	FormLabel,
	FormErrorMessage,
	Input,
	Select,
	Button,
	Card,
	CardBody,
	IconButton,
	Text,
	Badge,
	Switch,
	useColorModeValue,
	Box,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { CordeCommonModelsDtoContactContact as ContactDto, CordeCommonModelsDtoEntityEntity as EntityDto } from '@/api/generated/v1/api'
import { useContacts, ContactFormData } from '@/hooks/useContacts'
import { ContactCard } from './ContactCard'

// Icons
const AddIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
		<path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
	</svg>
)

const DeleteIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
		<path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
	</svg>
)

interface ContactFormProps {
	entity: EntityDto
	onContactAdded?: (contact: ContactDto) => void
	onContactsChanged?: () => void
}

const getContactTypes = (t: any) => [
	{ value: 'email', label: t('profile.contactTypes.email') },
	{ value: 'phone', label: t('profile.contactTypes.phone') },
	{ value: 'telegram', label: t('profile.contactTypes.telegram') },
	{ value: 'discord', label: t('profile.contactTypes.discord') },
	{ value: 'website', label: t('profile.contactTypes.website') },
	{ value: 'linkedin', label: t('profile.contactTypes.linkedin') },
	{ value: 'twitter', label: t('profile.contactTypes.twitter') },
	{ value: 'github', label: t('profile.contactTypes.github') },
]

const getContactTypeIcon = (type: string) => {
	switch (type?.toLowerCase()) {
		case 'email':
			return '📧'
		case 'phone':
			return '📱'
		case 'telegram':
			return '📞'
		case 'discord':
			return '💬'
		case 'instagram':
			return '📷'
		case 'twitter':
			return '🐦'
		default:
			return '📄'
	}
}

export const ContactForm: React.FC<ContactFormProps> = ({
	entity,
	onContactAdded,
	onContactsChanged,
}) => {
	const { t } = useTranslation()
	const {
		isLoading,
		createContact,
		listContacts,
		validateContactValue,
	} = useContacts()

	const [contacts, setContacts] = useState<ContactDto[]>([])
	const [newContact, setNewContact] = useState<ContactFormData>({
		type: 'email',
		value: '',
		label: '',
		isPrimary: false,
	})

	const [errors, setErrors] = useState<{
		value?: string
		general?: string
	}>({})

	const contactTypes = getContactTypes(t)

	const loadContacts = useCallback(async () => {
		try {
			const loadedContacts = await listContacts(entity.type, entity.id)
			setContacts(loadedContacts)
		} catch (error) {
			console.error('Failed to load contacts:', error)
		}
	}, [entity.type, entity.id, listContacts])

	// Load existing contacts
	useEffect(() => {
		loadContacts()
	}, [loadContacts])

	const validateForm = async (): Promise<boolean> => {
		const newErrors: typeof errors = {}

		if (!newContact.value.trim()) {
			newErrors.value = t('contacts.error.valueRequired')
			setErrors(newErrors)
			return false
		}

		// Check for duplicates
		const isDuplicate = contacts.some(contact => 
			contact.value?.toLowerCase() === newContact.value.toLowerCase() &&
			contact.type === newContact.type
		)

		if (isDuplicate) {
			newErrors.value = t('contacts.error.alreadyExists')
			setErrors(newErrors)
			return false
		}

		// Validate contact value format
		try {
			const validation = await validateContactValue(newContact.type, newContact.value)
			if (!validation.isValid) {
				newErrors.value = validation.message || t('contacts.error.invalidFormat')
				setErrors(newErrors)
				return false
			}
		} catch (error) {
			console.warn('Validation failed:', error)
		}

		setErrors({})
		return true
	}

	const handleSubmit = async () => {
		if (!(await validateForm())) return

		try {
			const createdContact = await createContact(entity, newContact)
			if (createdContact) {
				setNewContact({
					type: 'email',
					value: '',
					label: '',
					isPrimary: false,
				})
				await loadContacts()
				onContactAdded?.(createdContact)
				onContactsChanged?.()
			}
		} catch (error) {
			setErrors({ general: t('contacts.error.createFailed') })
		}
	}

	const handleContactsRefresh = () => {
		loadContacts()
		onContactsChanged?.()
	}

	return (
		<VStack spacing={6} align="stretch">
			{/* Add New Contact Form */}
			<Card variant="outline">
				<CardBody>
					<VStack spacing={4} align="stretch">
						<Text fontWeight="semibold" fontSize="md">
							{t('contacts.addNew')}
						</Text>

						<HStack spacing={4} align="end">
							<FormControl flex={1}>
								<FormLabel fontSize="sm">{t('contacts.type')}</FormLabel>
								<Select
									value={newContact.type}
									onChange={(e) => setNewContact(prev => ({ ...prev, type: e.target.value }))}
									size="sm"
								>
									{contactTypes.map(type => (
										<option key={type.value} value={type.value}>
											{getContactTypeIcon(type.value)} {type.label}
										</option>
									))}
								</Select>
							</FormControl>

							<FormControl flex={2} isInvalid={!!errors.value}>
								<FormLabel fontSize="sm">{t('contacts.value')}</FormLabel>
								<Input
									value={newContact.value}
									onChange={(e) => setNewContact(prev => ({ ...prev, value: e.target.value }))}
									placeholder={t('contacts.valuePlaceholder')}
									size="sm"
								/>
								{errors.value && (
									<FormErrorMessage>{errors.value}</FormErrorMessage>
								)}
							</FormControl>

							<FormControl flex={1}>
								<FormLabel fontSize="sm">{t('contacts.label')} ({t('common.optional')})</FormLabel>
								<Input
									value={newContact.label}
									onChange={(e) => setNewContact(prev => ({ ...prev, label: e.target.value }))}
									placeholder={t('contacts.labelPlaceholder')}
									size="sm"
								/>
							</FormControl>

							<FormControl>
								<FormLabel fontSize="sm">{t('contacts.isPrimary')}</FormLabel>
								<Switch
									isChecked={newContact.isPrimary}
									onChange={(e) => setNewContact(prev => ({ ...prev, isPrimary: e.target.checked }))}
									size="sm"
								/>
							</FormControl>

							<Button
								leftIcon={<AddIcon />}
								onClick={handleSubmit}
								isLoading={isLoading}
								colorScheme="blue"
								size="sm"
								minW="100px"
							>
								{t('contacts.add')}
							</Button>
						</HStack>

						{errors.general && (
							<Text color="red.500" fontSize="sm">
								{errors.general}
							</Text>
						)}
					</VStack>
				</CardBody>
			</Card>

			{/* Existing Contacts List */}
			{contacts.length > 0 && (
				<VStack spacing={3} align="stretch">
					<Text fontWeight="semibold" fontSize="md">
						{t('contacts.existing')} ({contacts.length})
					</Text>

					{contacts.map((contact) => (
						<ContactCard
							key={contact.id}
							contact={contact}
							entity={entity}
							isEditable={true}
							onContactUpdated={handleContactsRefresh}
							onContactDeleted={handleContactsRefresh}
						/>
					))}
				</VStack>
			)}
		</VStack>
	)
} 